import { deleteSubscription, findDeviceByEndpoint } from "./push-service.js";
import {
  currentEndpoint,
  isPushSupported,
  unsubscribeThisDevice,
} from "./subscription.js";

/**
 * How long the server half may take before sign-out gives up waiting on it.
 *
 * The WSC client allows 30 seconds per request and this path makes two, so
 * without a bound a wedged server holds the session open for a minute. Signing
 * out is the one thing that must not depend on the network being healthy.
 */
const FORGET_TIMEOUT_MS = 3000;

/**
 * Drops this device's push registration, both halves of it.
 *
 * Must be called while the session is still valid, since deleting the row is an
 * authenticated request. A device left registered after sign-out would keep
 * delivering the previous user's agenda to a machine they have signed out of.
 *
 * The browser half goes first so it does not depend on the server half
 * succeeding. Account deletion runs through here with the membership already
 * gone, where the lookup can only fail.
 * @returns {Promise<void>} Resolves once the device has been forgotten
 */
async function forget() {
  const endpoint = await currentEndpoint();
  if (!endpoint) return;

  await unsubscribeThisDevice();

  const device = await findDeviceByEndpoint(endpoint);

  if (device) await deleteSubscription(device.id);
}

/**
 * Forgets this device's push registration, giving up if the server is slow.
 *
 * Abandoning the wait does not cancel the request. Once the token is cleared the
 * WSC client turns the resulting 401 into a cancellation, which is swallowed
 * below along with every other failure: nothing here is worth trapping someone
 * in a session they asked to leave.
 * @returns {Promise<void>} Resolves once the device is forgotten or time is up
 */
export async function forgetThisDevice() {
  if (!isPushSupported()) return;

  await Promise.race([
    forget().catch((error) => {
      console.error("Failed to forget this device's push subscription", error);
    }),
    new Promise((resolve) => setTimeout(resolve, FORGET_TIMEOUT_MS)),
  ]);
}
